/**
 * Contracts for module "shell". Owner: ui-shell.
 * Portal shell config (one theme key, QM-D02-003 / QM-NC02-001), per-user UI preferences (QM-NC02-003),
 * global search (QM-D02-004), RUM beacons (QM-NX-003) and the device cache partition (QM-D19-003).
 */
import { z } from 'zod';

/** The one approved theme key. Quixo surfaces never take tenant tokens (white-label is partner-client only). */
export const THEME_KEY = 'quixo-marketing' as const;
export const ShellSurfaceSchema = z.enum(['customer', 'partner', 'admin']);
export type ShellSurface = z.infer<typeof ShellSurfaceSchema>;

export const ThemePreferenceSchema = z.enum(['system', 'light', 'dark']);
export const MotionPreferenceSchema = z.enum(['system', 'reduce', 'no-preference']);
export const UiPreferencesSchema = z.object({
  theme: ThemePreferenceSchema.default('system'),
  motion: MotionPreferenceSchema.default('system'),
}).strict();
export type UiPreferences = z.infer<typeof UiPreferencesSchema>;
export const DEFAULT_UI_PREFERENCES: UiPreferences = { theme: 'system', motion: 'system' };

export const ShellConfigSchema = z.object({
  themeKey: z.literal(THEME_KEY),
  brand: z.literal(THEME_KEY),
  surface: ShellSurfaceSchema,
  tokensEditable: z.literal(false),
  whiteLabel: z.literal(false),
  preferences: UiPreferencesSchema,
});
export type ShellConfig = z.infer<typeof ShellConfigSchema>;

// ---------- global search ----------
export const SEARCH_KINDS = ['lead', 'customer', 'conversation', 'approval', 'deliverable', 'report', 'invoice', 'calendar_event', 'campaign', 'asset'] as const;
export const SearchKindSchema = z.enum(SEARCH_KINDS);
export type SearchKind = z.infer<typeof SearchKindSchema>;

export const SearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(100),
  kind: SearchKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const SearchHitSchema = z.object({
  id: z.string(),
  kind: SearchKindSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  /** portal-relative path, e.g. /approvals/apr_123 */
  path: z.string().regex(/^\/[A-Za-z0-9/_-]*$/),
  updatedAt: z.string(),
});
export type SearchHit = z.infer<typeof SearchHitSchema>;

/**
 * Other modules publish searchable records with a SYSTEM_QUEUE message
 * `{ type: 'shell.search.upsert' | 'shell.search.delete', tenantId, idempotencyKey, payload }`
 * or POST /v1/shell/internal/search-index (system role). Titles only; no message bodies or PII beyond a name.
 */
export const SearchIndexUpsertSchema = z.object({
  op: z.enum(['upsert', 'delete']).default('upsert'),
  tenantId: z.string().min(1),
  kind: SearchKindSchema,
  sourceId: z.string().min(1).max(64),
  title: z.string().max(200).default(''),
  subtitle: z.string().max(200).nullable().optional(),
  path: z.string().regex(/^\/[A-Za-z0-9/_-]*$/).default('/'),
  /** roles allowed to see the hit; empty = every member of the tenant */
  roles: z.array(z.string()).max(20).default([]),
});
export type SearchIndexUpsert = z.infer<typeof SearchIndexUpsertSchema>;

// ---------- RUM (QM-NX-003). Strict: nothing personal, no tenant names, no ids in routes ----------
export const RUM_METRICS = ['LCP', 'INP', 'CLS', 'FCP', 'TTFB'] as const;
/** Route pattern only (ids replaced by :id). Letters, digits, '/', ':', '-' only. */
export const RumRouteSchema = z.string().max(80).regex(/^\/[a-z0-9/:-]*$/);
export const RumBeaconSchema = z.object({
  metric: z.enum(RUM_METRICS),
  value: z.number().min(0).max(120_000),
  route: RumRouteSchema,
  device: z.enum(['mobile', 'tablet', 'desktop']),
  connection: z.enum(['4g', '3g', 'slow-2g', '2g', 'wifi', 'unknown']).default('unknown'),
}).strict();
export type RumBeacon = z.infer<typeof RumBeaconSchema>;
export const RumBatchSchema = z.object({ beacons: z.array(RumBeaconSchema).min(1).max(20) }).strict();

/** Web-vitals budgets (NFR X-03). INP is measured in the field; Lighthouse uses TBT as its lab proxy. */
export const VITALS_BUDGET = { LCP: 2500, INP: 200, CLS: 0.1 } as const;

export const CachePartitionSchema = z.object({ partition: z.string().regex(/^[a-f0-9]{32}$/), clearOnSignOut: z.literal(true) });
